import React, { useState } from "react";
import {
  Box,
  Text,
  Flex,
  Avatar,
  AvatarGroup,
  Button,
  useColorModeValue,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  VStack,
  useDisclosure,
  useToast,
  Input,
} from "@chakra-ui/react";
import { ArrowBackIcon, EditIcon, AddIcon, CheckIcon } from "@chakra-ui/icons";
import { FaRedo } from "react-icons/fa";
import { getBid, cqAPI } from "../../lib/Auth";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../../AppContext";
import AsyncSelect from "react-select/async";

function GroupChatInfo({
  group,
  groupId,
  addActionBarItem,
  removeActionBarItem,
  isMobile,
  onGroupUpdated,
  conversationClosed,
  setConversationClosed,
}) {
  const bid = getBid();
  const navigate = useNavigate();
  const toast = useToast();
  const bgColor = useColorModeValue("#D8DEDF", "#303343");
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const { isOpen: isAddOpen, onOpen: onAddOpen, onClose: onAddClose } = useDisclosure();
  const [editName, setEditName] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);

  const onClose = () => {
    navigate(`/team?bid=${bid}`);
  };

  const handleRename = async () => {
    if (!editName.trim()) return;
    const response = await cqAPI.put(`/group-chat/${groupId}`, {
      name: editName.trim(),
    });
    if (response.type === "ok") {
      toast({ title: "Group renamed", status: "success", duration: 2000 });
      onEditClose();
      onGroupUpdated && onGroupUpdated();
    } else {
      toast({ title: "Error", description: response.msg, status: "error", duration: 3000 });
    }
  };

  const handleAddParticipant = async () => {
    if (!selectedUser) return;
    const response = await cqAPI.put(`/group-chat/${groupId}/participants`, {
      add: [selectedUser.value],
    });
    if (response.type === "ok") {
      toast({ title: "Participant added", status: "success", duration: 2000 });
      setSelectedUser(null);
      onAddClose();
      onGroupUpdated && onGroupUpdated();
    } else {
      toast({ title: "Error", description: response.msg, status: "error", duration: 3000 });
    }
  };

  const handleRemoveParticipant = async (uid) => {
    const response = await cqAPI.put(`/group-chat/${groupId}/participants`, {
      remove: [uid],
    });
    if (response.type === "ok") {
      toast({ title: "Participant removed", status: "success", duration: 2000 });
      onGroupUpdated && onGroupUpdated();
    } else {
      toast({ title: "Error", description: response.msg, status: "error", duration: 3000 });
    }
  };

  const handleLeaveGroup = async () => {
    const response = await cqAPI.delete(`/group-chat/${groupId}/leave`);
    if (response.type === "ok") {
      navigate(`/team?bid=${bid}`);
    } else {
      toast({ title: "Error", description: response.msg, status: "error", duration: 3000 });
    }
  };

  const toggleConversationStatus = async () => {
    const response = await cqAPI.put(`/group-chat/${groupId}/close`, {
      closed: !conversationClosed,
    });
    if (response.type === "ok") {
      setConversationClosed(!conversationClosed);
    } else {
      toast({ title: "Error", description: response.msg, status: "error", duration: 3000 });
    }
  };

  const loadStaffOptions = async (inputValue) => {
    try {
      const response = await cqAPI.get(`/a-dec/findProfiles?query=${inputValue}`);
      if (response.type === "ok") {
        return (response.data || [])
          .filter(p => {
            // Exclude users already in the group
            if (!group) return true;
            return !group.participants.some(gp => String(gp._id) === String(p.id));
          })
          .map(p => ({
            value: p.id,
            label: p.name || p.profile_name || p.email,
          }));
      }
      return [];
    } catch {
      return [];
    }
  };

  if (!group) return null;

  return (
    <>
      <Box bg={bgColor} p={2} borderRadius="12px 12px 0px 0px">
        <Flex align="center">
          <Button
            variant="ghost"
            leftIcon={<ArrowBackIcon />}
            onClick={onClose}
            size="sm"
          />
          <AvatarGroup size="sm" max={3} ml={2}>
            {group.participants.map((p) => (
              <Avatar key={String(p._id)} name={p.name} src={p.pic} />
            ))}
          </AvatarGroup>
          <VStack spacing={0} ml={3} align="flex-start" flex={1}>
            <Text fontWeight="600" fontSize="md">
              {group.name}
            </Text>
            <Text fontSize="xs" color="gray.500">
              {group.participants.map((p) => p.name).join(", ")}
            </Text>
          </VStack>
          <IconButton
            icon={<EditIcon />}
            size="sm"
            variant="ghost"
            onClick={() => {
              setEditName(group.name);
              onEditOpen();
            }}
            mr={1}
          />
          <IconButton
            icon={<AddIcon />}
            size="sm"
            variant="ghost"
            onClick={onAddOpen}
            mr={1}
          />
          <Button
            leftIcon={conversationClosed ? <FaRedo /> : <CheckIcon />}
            size="sm"
            variant="solid"
            onClick={toggleConversationStatus}
            mr={2}
          >
            {conversationClosed ? "Open" : "Close"} chat
          </Button>
          <Button
            size="sm"
            variant="outline"
            colorScheme="red"
            onClick={handleLeaveGroup}
          >
            Leave
          </Button>
        </Flex>
      </Box>

      {/* Rename Modal */}
      <Modal isOpen={isEditOpen} onClose={onEditClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Rename Group</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder="Group name"
              onKeyDown={(e) => e.key === "Enter" && handleRename()}
            />
            <Button mt={3} colorScheme="blue" onClick={handleRename} w="100%">
              Save
            </Button>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Add Participant Modal */}
      <Modal isOpen={isAddOpen} onClose={onAddClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add Participant</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <AsyncSelect
              cacheOptions
              defaultOptions
              loadOptions={loadStaffOptions}
              onChange={setSelectedUser}
              placeholder="Search staff..."
              menuPlacement="auto"
            />
            <Button
              mt={3}
              colorScheme="blue"
              onClick={handleAddParticipant}
              isDisabled={!selectedUser}
              w="100%"
            >
              Add
            </Button>

            {/* Current participants list */}
            <Text mt={4} mb={2} fontWeight="600" fontSize="sm">
              Current Members
            </Text>
            <VStack align="stretch" spacing={2}>
              {group.participants.map((p) => (
                <Flex key={String(p._id)} align="center" justify="space-between">
                  <Flex align="center">
                    <Avatar size="xs" name={p.name} src={p.pic} mr={2} />
                    <Text fontSize="sm">{p.name}</Text>
                  </Flex>
                  <Button
                    size="xs"
                    variant="ghost"
                    colorScheme="red"
                    onClick={() => handleRemoveParticipant(String(p._id))}
                  >
                    Remove
                  </Button>
                </Flex>
              ))}
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}

export default GroupChatInfo;
